import { useCallback, useState } from "react";
import { Loader2, UserMinus, UserPlus } from "lucide-react";
import { Button, Input } from "@cieda/djayma-ui";
import { toast } from "sonner";

import { ADMIN_THEME } from "@/lib/admin-theme";
import {
  useDeactivateAdmin,
  useInviteAdmin,
  useTeam,
} from "@/hooks/admin/useTeam";
import type { InviteAdminInput } from "@/lib/admin-api/team";

import { RoleBadge, StatusBadge } from "./badges";
import { EMPTY_INVITE_FORM } from "./constants";
import { InviteAdminModal } from "./InviteAdminModal";
import type { InviteFormState } from "./types";

// ---------------------------------------------------------------------------
// Tab 3: Administrators
// ---------------------------------------------------------------------------

export function AdministratorsTab() {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<InviteFormState>(EMPTY_INVITE_FORM);

  const { data: teamData, isLoading: teamLoading } = useTeam({ q: search || undefined });
  const inviteAdmin = useInviteAdmin();
  const deactivateAdmin = useDeactivateAdmin();

  const admins = teamData?.admins ?? [];
  const total = teamData?.pagination.total ?? 0;

  const updateForm = useCallback(
    <K extends keyof InviteFormState>(field: K, value: InviteFormState[K]) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleInvite = useCallback(async () => {
    if (!form.email.trim()) {
      toast.error("Veuillez saisir une adresse email.");
      return;
    }
    if (!form.full_name.trim()) {
      toast.error("Veuillez saisir le nom complet.");
      return;
    }
    if (!form.department_id) {
      toast.error("Veuillez sélectionner un département.");
      return;
    }
    const payload: InviteAdminInput = {
      email: form.email.trim(),
      full_name: form.full_name.trim(),
      role: form.role,
      department_id: form.department_id,
      platform: form.platform,
    };
    await inviteAdmin.mutateAsync(payload);
    setShowInviteModal(false);
    setForm(EMPTY_INVITE_FORM);
  }, [form, inviteAdmin]);

  const handleDeactivate = useCallback(
    (id: string, name: string) => {
      if (!confirm(`Désactiver ${name} ?`)) return;
      deactivateAdmin.mutate(id);
    },
    [deactivateAdmin],
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <p className="text-sm text-stone-500">
            {teamLoading ? "Chargement..." : `${total} administrateur${total > 1 ? "s" : ""} actif${total > 1 ? "s" : ""}`}
          </p>
          <Input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48 border-stone-200 bg-stone-50 text-sm focus:border-stone-300"
          />
        </div>
        <Button
          onClick={() => setShowInviteModal(true)}
          className="gap-2 text-white"
          style={{ background: ADMIN_THEME.accent.green }}
        >
          <UserPlus className="h-4 w-4" />
          Inviter un administrateur
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border bg-white" style={{ borderColor: "#e7e0d6" }}>
        {teamLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
          </div>
        ) : admins.length === 0 ? (
          <div className="py-16 text-center text-sm text-stone-400">
            Aucun administrateur trouvé.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-stone-50/60" style={{ borderColor: "#e7e0d6" }}>
                  <th className="px-4 py-3 text-left font-medium text-stone-600">Nom</th>
                  <th className="px-4 py-3 text-left font-medium text-stone-600">Email</th>
                  <th className="px-4 py-3 text-left font-medium text-stone-600">Rôle</th>
                  <th className="px-4 py-3 text-left font-medium text-stone-600">Département</th>
                  <th className="px-4 py-3 text-left font-medium text-stone-600">Statut</th>
                  <th className="px-4 py-3 text-left font-medium text-stone-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr
                    key={admin.id}
                    className="border-b last:border-0 transition-colors hover:bg-stone-50/40"
                    style={{ borderColor: "#f0ebe4" }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold"
                          style={{
                            background: "rgba(199,91,57,0.1)",
                            color: ADMIN_THEME.sidebar.bg,
                          }}
                        >
                          {admin.full_name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                        <span className="font-medium text-stone-800">{admin.full_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-stone-600">{admin.email}</td>
                    <td className="px-4 py-3">
                      <RoleBadge role={admin.role} />
                    </td>
                    <td className="px-4 py-3 text-stone-500">{admin.department_name}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={admin.is_active ? "Actif" : "Inactif"} />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleDeactivate(admin.id, admin.full_name)}
                        disabled={deactivateAdmin.isPending}
                        className="rounded p-1 text-stone-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-40"
                        title="Désactiver"
                      >
                        <UserMinus className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite modal */}
      {showInviteModal && (
        <InviteAdminModal
          form={form}
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleInvite}
          onUpdateField={updateForm}
          isSubmitting={inviteAdmin.isPending}
        />
      )}
    </div>
  );
}
